import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { RefreshCw, Search, Download, Plus, LayoutList, Columns } from 'lucide-react';
import {
  searchCrm,
  exportCrmCsv,
  type CrmLeadCard,
  type CrmPipelineCard,
  type CrmSearchResult,
  type SalesStage,
} from '../../../api';
import { AdminGlassDisclosure } from '../AdminGlassDisclosure';
import { CrmNewLeadModal } from './CrmNewLeadModal';
import { SALES_STAGE_META, SALES_STAGE_ORDER_ACTIVE } from './crmLabels';
import type { AdminManagerBrief } from '../../../api';
import { canCrmFullControl, canCrmManageLeads, type CrmAccessParams } from '../../../crmAccess';
import { useCrmPipeline, useInvalidateCrm } from '../../../hooks/crm/useCrmQueries';
import { formatAdminPanelError } from '@/lib/adminApiError';
import { CrmInlineError } from './CrmInlineError';
import type { CrmOpenDealHandler } from './crmDealDeepLink';
import { CrmDealStrip, nextStepBadgesForLead, nextStepBadgesForOrder } from './crmDealStrip';

type Props = {
  access: CrmAccessParams;
  hapticSelection: () => void;
  hapticSuccess: () => void;
  onOpenDeal?: CrmOpenDealHandler;
  initialOrderId?: number | null;
  initialLeadId?: number | null;
  managerId?: number | null;
  crmDepartment?: string | null;
  managers?: AdminManagerBrief[];
  /** Desktop: allow Kanban toggle */
  kanbanSlot?: ReactNode;
};

function managerLabel(
  managerId: number | null | undefined,
  managers?: AdminManagerBrief[],
): string | null {
  if (!managerId || !managers?.length) return null;
  const m = managers.find((x) => x.id === managerId);
  if (!m) return `#${managerId}`;
  return (m.first_name || m.username || `#${m.id}`).trim();
}

export function CrmPipelineStageList({
  access,
  hapticSelection,
  hapticSuccess,
  onOpenDeal,
  initialOrderId,
  initialLeadId,
  managerId,
  crmDepartment,
  managers,
  kanbanSlot,
}: Props) {
  const crmFull = canCrmFullControl(access);
  const canLeads = canCrmManageLeads(access);
  const [mineOnly, setMineOnly] = useState(false);
  const [showNewLead, setShowNewLead] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchHits, setSearchHits] = useState<CrmSearchResult[]>([]);
  const [desktop, setDesktop] = useState(false);
  const [view, setView] = useState<'list' | 'kanban'>('list');
  const invalidate = useInvalidateCrm();

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const apply = () => {
      const isDesktop = mq.matches;
      setDesktop(isDesktop);
      if (kanbanSlot) setView(isDesktop ? 'kanban' : 'list');
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [kanbanSlot]);

  const effectiveManagerId = crmFull && !mineOnly ? (managerId ?? undefined) : undefined;
  const { data, isLoading, isError, error, refetch, isFetching } = useCrmPipeline({
    mineOnly: mineOnly || !crmFull,
    managerId: effectiveManagerId ?? null,
    includeUnassigned: true,
    crmDepartment: crmDepartment ?? null,
  });

  const leads = data?.leads ?? [];
  const lostOrders = data?.columns?.lost ?? [];
  const initialOrderOpenedRef = useRef<number | null>(null);
  const initialLeadOpenedRef = useRef<number | null>(null);

  useEffect(() => {
    initialOrderOpenedRef.current = null;
  }, [initialOrderId]);

  useEffect(() => {
    initialLeadOpenedRef.current = null;
  }, [initialLeadId]);

  useEffect(() => {
    if (!data || !initialOrderId || !onOpenDeal) return;
    if (initialOrderOpenedRef.current === initialOrderId) return;
    initialOrderOpenedRef.current = initialOrderId;
    for (const cards of Object.values(data.columns ?? {})) {
      const card = cards.find((c) => c.id === initialOrderId);
      if (card) {
        onOpenDeal({ kind: 'order', id: card.id }, { orderCard: card });
        return;
      }
    }
    onOpenDeal({ kind: 'order', id: initialOrderId });
  }, [data, initialOrderId, onOpenDeal]);

  useEffect(() => {
    if (!data?.leads || !initialLeadId || !onOpenDeal) return;
    if (initialLeadOpenedRef.current === initialLeadId) return;
    initialLeadOpenedRef.current = initialLeadId;
    const lead = data.leads.find((l) => l.id === initialLeadId);
    if (lead) onOpenDeal({ kind: 'lead', id: lead.id }, { leadCard: lead });
    else onOpenDeal({ kind: 'lead', id: initialLeadId });
  }, [data?.leads, initialLeadId, onOpenDeal]);

  useEffect(() => {
    if (searchQ.trim().length < 2) {
      setSearchHits([]);
      return;
    }
    const t = setTimeout(() => {
      void searchCrm(searchQ.trim()).then((r) => setSearchHits(r.results));
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  const openSearchHit = (hit: CrmSearchResult) => {
    hapticSelection();
    if (hit.type === 'order') {
      for (const cards of Object.values(data?.columns ?? {})) {
        const c = cards.find((x) => x.id === hit.id);
        if (c) {
          onOpenDeal?.({ kind: 'order', id: c.id }, { orderCard: c });
          setSearchQ('');
          setSearchHits([]);
          return;
        }
      }
      onOpenDeal?.({ kind: 'order', id: hit.id });
    } else {
      const ld = data?.leads?.find((l) => l.id === hit.id);
      if (ld) onOpenDeal?.({ kind: 'lead', id: ld.id }, { leadCard: ld });
      else onOpenDeal?.({ kind: 'lead', id: hit.id });
    }
    setSearchQ('');
    setSearchHits([]);
  };

  const onRefresh = () => {
    hapticSelection();
    void refetch();
    void invalidate();
  };

  const downloadExport = async () => {
    hapticSelection();
    try {
      const csv = await exportCrmCsv({ manager_id: effectiveManagerId, days: 90 });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'crm_export.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  };

  const stageSummaries = useMemo(() => {
    return SALES_STAGE_ORDER_ACTIVE.map((stage) => {
      const cards = stage === 'lead_received' ? (data?.columns[stage] ?? []) : (data?.columns[stage] ?? []);
      const leadCount = stage === 'lead_received' ? leads.length : 0;
      const orderCount = cards.length;
      const count = stage === 'lead_received' ? leadCount + orderCount : orderCount;
      const sum = cards.reduce((acc, c) => acc + (c.final_amount || 0), 0);
      return { stage, count, sum, cards, leadCount, orderCount };
    });
  }, [data?.columns, leads.length]);

  const toolbar = (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="relative flex-1 min-w-[140px]">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" color="var(--text-3)" />
        <input
          className="input-glass w-full text-sm pl-9 min-h-[44px] touch-manipulation"
          placeholder="Клиент, телефон, №…"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
        />
        {searchHits.length > 0 && (
          <div
            className="absolute z-20 left-0 right-0 mt-1 rounded-xl overflow-hidden max-h-48 overflow-y-auto"
            style={{ background: 'rgba(10,25,47,0.98)', border: '1px solid var(--border-subtle)' }}
          >
            {searchHits.map((h) => (
              <button
                key={`${h.type}-${h.id}`}
                type="button"
                onClick={() => openSearchHit(h)}
                className="press w-full text-left px-3 py-3 text-xs min-h-[44px] touch-manipulation"
                style={{ borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-1)' }}
              >
                <span className="font-bold">{h.label}</span>
                <span className="block text-[10px]" style={{ color: 'var(--text-3)' }}>{h.subtitle}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {desktop && kanbanSlot && (
        <div className="flex rounded-lg overflow-hidden shrink-0" style={{ border: '1px solid var(--border-subtle)' }}>
          <button
            type="button"
            onClick={() => {
              hapticSelection();
              setView('list');
            }}
            className="press flex items-center gap-1 px-2.5 py-2 text-[10px] font-bold min-h-[44px] touch-manipulation"
            style={
              view === 'list'
                ? { background: 'rgba(99,179,237,0.2)', color: '#63B3ED' }
                : { background: 'rgba(255,255,255,0.04)', color: 'var(--text-3)' }
            }
          >
            <LayoutList size={14} /> Список
          </button>
          <button
            type="button"
            onClick={() => {
              hapticSelection();
              setView('kanban');
            }}
            className="press flex items-center gap-1 px-2.5 py-2 text-[10px] font-bold min-h-[44px] touch-manipulation border-l"
            style={
              view === 'kanban'
                ? { background: 'rgba(99,179,237,0.2)', color: '#63B3ED', borderColor: 'var(--border-subtle)' }
                : { background: 'rgba(255,255,255,0.04)', color: 'var(--text-3)', borderColor: 'var(--border-subtle)' }
            }
          >
            <Columns size={14} /> Колонки
          </button>
        </div>
      )}
      {canLeads && (
        <button
          type="button"
          onClick={() => {
            hapticSelection();
            setShowNewLead(true);
          }}
          className="press p-2.5 rounded-lg shrink-0 min-h-[44px] min-w-[44px] touch-manipulation"
          style={{ background: 'rgba(72,187,120,0.2)', border: '1px solid rgba(72,187,120,0.35)' }}
          aria-label="Новый лид"
        >
          <Plus size={18} color="#68D391" />
        </button>
      )}
      {crmFull && (
        <button
          type="button"
          onClick={() => void downloadExport()}
          className="press p-2.5 rounded-lg shrink-0 min-h-[44px] touch-manipulation"
          style={{ background: 'rgba(255,255,255,0.06)' }}
          aria-label="Экспорт CSV"
        >
          <Download size={16} color="var(--cyan)" />
        </button>
      )}
      {crmFull && (
        <button
          type="button"
          onClick={() => {
            hapticSelection();
            setMineOnly((v) => !v);
          }}
          className="press px-3 py-2 rounded-lg text-[11px] font-bold shrink-0 min-h-[44px] touch-manipulation"
          style={
            mineOnly
              ? { background: 'rgba(99,179,237,0.2)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.35)' }
              : { background: 'rgba(255,255,255,0.04)', color: 'var(--text-3)', border: '1px solid var(--border-subtle)' }
          }
        >
          {mineOnly ? 'Мои' : managerId ? 'Команда' : 'Все'}
        </button>
      )}
      <button
        type="button"
        onClick={onRefresh}
        className="press p-2.5 rounded-lg shrink-0 min-h-[44px] touch-manipulation"
        style={{ background: 'rgba(255,255,255,0.06)' }}
      >
        <RefreshCw size={16} color="var(--cyan)" className={isLoading || isFetching ? 'animate-spin' : ''} />
      </button>
    </div>
  );

  if (desktop && view === 'kanban' && kanbanSlot) {
    return (
      <div className="space-y-3">
        {toolbar}
        {kanbanSlot}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {toolbar}

      {isLoading && !data && (
        <p className="text-xs py-4 text-center" style={{ color: 'var(--text-3)' }}>
          Загрузка воронки…
        </p>
      )}

      {isError && (
        <CrmInlineError
          message={formatAdminPanelError(error, 'Не удалось загрузить воронку')}
          onRetry={() => void refetch()}
        />
      )}

      {stageSummaries.map(({ stage, count, sum, cards, leadCount, orderCount }) => {
        if (count === 0) return null;
        const meta = SALES_STAGE_META[stage];
        const accentMap: Record<string, 'cyan' | 'emerald' | 'amber' | 'coral' | 'violet' | 'slate'> = {
          lead_received: 'amber',
          negotiation: 'cyan',
          invoice_sent: 'violet',
          paid: 'emerald',
          measurement: 'amber',
          measurement_done: 'cyan',
          compose: 'violet',
          production: 'violet',
          ready_to_ship: 'emerald',
          fulfillment: 'cyan',
        };
        return (
          <AdminGlassDisclosure
            key={stage}
            accent={accentMap[stage] ?? 'cyan'}
            defaultOpen={false}
            density="compact"
            title={meta.label}
            subtitle={
              stage === 'lead_received' && leadCount > 0 && orderCount > 0
                ? `${leadCount} лид · ${orderCount} заказ`
                : `${count} сделок`
            }
            badge={
              sum > 0 ? (
                <span className="text-[10px] font-bold" style={{ color: 'var(--cyan-bright)' }}>
                  {Math.round(sum).toLocaleString('ru-RU')} ₽
                </span>
              ) : count
            }
          >
            <StageDeals
              stage={stage}
              cards={cards}
              leads={stage === 'lead_received' ? leads : []}
              managers={managers}
              hapticSelection={hapticSelection}
              onOpenDeal={onOpenDeal}
            />
          </AdminGlassDisclosure>
        );
      })}

      {lostOrders.length > 0 && (
        <AdminGlassDisclosure
          accent="slate"
          defaultOpen={false}
          density="compact"
          title="Потерянные"
          subtitle={`${lostOrders.length} заказов`}
          badge={lostOrders.length}
        >
          <div className="space-y-1.5">
            {lostOrders.map((card) => (
              <OrderStrip key={card.id} card={card} managers={managers} hapticSelection={hapticSelection} onOpenDeal={onOpenDeal} />
            ))}
          </div>
        </AdminGlassDisclosure>
      )}

      {!isLoading && stageSummaries.every((s) => s.count === 0) && lostOrders.length === 0 && (
        <p className="text-xs py-6 text-center" style={{ color: 'var(--text-3)' }}>
          Воронка пуста
        </p>
      )}

      {showNewLead && (
        <CrmNewLeadModal
          onClose={() => setShowNewLead(false)}
          onCreated={onRefresh}
          onOpenExisting={(id) => onOpenDeal?.({ kind: 'lead', id })}
          hapticSelection={hapticSelection}
          hapticSuccess={hapticSuccess}
        />
      )}
    </div>
  );
}

function StageDeals({
  stage,
  cards,
  leads,
  managers,
  hapticSelection,
  onOpenDeal,
}: {
  stage: SalesStage;
  cards: CrmPipelineCard[];
  leads: CrmLeadCard[];
  managers?: AdminManagerBrief[];
  hapticSelection: () => void;
  onOpenDeal?: CrmOpenDealHandler;
}) {
  if (stage === 'lead_received' && leads.length > 0 && cards.length > 0) {
    return (
      <div className="space-y-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5 px-0.5" style={{ color: '#68D391' }}>
            Лиды
          </p>
          <div className="space-y-1.5">
            {leads.map((lead) => (
              <LeadStrip key={lead.id} lead={lead} hapticSelection={hapticSelection} onOpenDeal={onOpenDeal} />
            ))}
          </div>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5 px-0.5" style={{ color: '#63B3ED' }}>
            Заказы
          </p>
          <div className="space-y-1.5">
            {cards.map((card) => (
              <OrderStrip key={card.id} card={card} managers={managers} hapticSelection={hapticSelection} onOpenDeal={onOpenDeal} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (stage === 'lead_received' && leads.length > 0) {
    return (
      <div className="space-y-1.5">
        {leads.map((lead) => (
          <LeadStrip key={lead.id} lead={lead} hapticSelection={hapticSelection} onOpenDeal={onOpenDeal} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {cards.map((card) => (
        <OrderStrip key={card.id} card={card} managers={managers} hapticSelection={hapticSelection} onOpenDeal={onOpenDeal} />
      ))}
    </div>
  );
}

function OrderStrip({
  card,
  managers,
  hapticSelection,
  onOpenDeal,
}: {
  card: CrmPipelineCard;
  managers?: AdminManagerBrief[];
  hapticSelection: () => void;
  onOpenDeal?: CrmOpenDealHandler;
}) {
  const client = card.user.first_name || card.user.username || `#${card.user.id}`;
  return (
    <CrmDealStrip
      title={card.order_number}
      sub={client}
      amount={card.final_amount}
      managerLabel={managerLabel(card.assigned_manager_id, managers)}
      badges={nextStepBadgesForOrder(card)}
      onClick={() => {
        hapticSelection();
        onOpenDeal?.({ kind: 'order', id: card.id }, { orderCard: card });
      }}
    />
  );
}

function LeadStrip({
  lead,
  hapticSelection,
  onOpenDeal,
}: {
  lead: CrmLeadCard;
  hapticSelection: () => void;
  onOpenDeal?: CrmOpenDealHandler;
}) {
  return (
    <CrmDealStrip
      title={lead.title}
      sub={lead.client_name || lead.phone || 'Лид'}
      badges={nextStepBadgesForLead(lead)}
      onClick={() => {
        hapticSelection();
        onOpenDeal?.({ kind: 'lead', id: lead.id }, { leadCard: lead });
      }}
    />
  );
}
